import { useEffect, useRef } from 'react'
import type { Chart } from 'klinecharts'
import { indicatorByName } from './catalog'
import { applyIndicator, ensureIndicatorClone, mintEngineName } from './indicators'
import type { SymbolSliceState } from './workbenchState'

/**
 * Recreate the workbench's indicators on the walkthrough's replay chart.
 *
 * Reuses the same three-step recipe as Workbench's own restore (mintEngineName →
 * ensureIndicatorClone → applyIndicator), in the same saved-array order, so duplicate
 * instances land on the same engine names they would on the canvas. Rebuilding it here
 * instead would let the two charts' indicator sets diverge for no reason.
 *
 * The instances are not tracked in React state: nothing in walkthrough mode can add, remove
 * or restyle an indicator, so there is nothing to re-render for. They die with the chart.
 */
export default function useWalkthroughIndicators(
  chart: Chart | null,
  indicators: SymbolSliceState['indicators'] | null,
): void {
  // Applied engine names, so a re-run removes exactly what it created.
  const appliedRef = useRef<Array<{ engineName: string; paneId: string; bandIds: string[] }>>([])

  useEffect(() => {
    if (!chart || !indicators || indicators.length === 0) return undefined

    const applied: Array<{ engineName: string; paneId: string; bandIds: string[] }> = []

    for (const entry of indicators) {
      const option = indicatorByName(entry.name)
      if (!option) continue

      const engineName = mintEngineName(option.name, applied.map((i) => i.engineName))
      if (engineName !== option.name) {
        const src = applied.find((i) => i.engineName === option.name)
        if (!src || !ensureIndicatorClone(chart, src, engineName, option.name)) continue
      }

      try {
        const res = applyIndicator(chart, option, entry.settings, engineName)
        if (res) applied.push({ engineName, paneId: res.paneId, bandIds: res.bandIds })
      } catch {
        // One bad saved indicator must not take the whole walkthrough down.
      }
    }

    appliedRef.current = applied

    return () => {
      for (const inst of applied) {
        try {
          for (const id of inst.bandIds) chart.removeOverlay(id)
          chart.removeIndicator(inst.paneId, inst.engineName)
        } catch {
          /* engine already disposed */
        }
      }
      appliedRef.current = []
    }
  }, [chart, indicators])
}
